import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Coffee, Users, Calendar, MessageCircle, Mail, Copy, X, CalendarDays, Footprints, Sparkles, Bell, Check, ExternalLink, Utensils, Palette } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { toast } from "sonner";
import { EventStatusBadge } from "@/components/event-status-badge";
import { CustomizeCafeSection } from "@/components/customize-cafe-section";
import { CafeMenuManager } from "@/components/cafe-menu-manager";
import { useAppContexts } from "@/hooks/use-app-context";

export const Route = createFileRoute("/_authenticated/cafe/dashboard")({
  head: () => ({ meta: [{ title: "Dashboard cafetería — Brulo" }] }),
  component: CafeDashboard,
});


type EventRow = {
  id: string;
  name: string;
  event_date: string;
  event_time: string;
  meeting_point: string;
  estado_confirmacion: "pendiente" | "confirmado" | "declinado";
  created_at: string;
  organizador_tipo: "club" | "cafeteria";
  clubs: { name: string } | null;
};

type MyCafeEvent = {
  id: string;
  name: string;
  event_date: string;
  event_time: string;
  tipo_actividad: "running" | "ciclismo" | "ambos" | "social";
  invitaciones: { club_id: string; estado: "pendiente" | "aceptado" | "declinado"; clubs: { name: string } | null }[];
};

type TopClub = {
  id: string;
  name: string;
  city: string | null;
  phone: string | null;
  contact_email: string | null;
  owner_id: string;
  organizer_name: string | null;
  visits: number;
};

function CafeDashboard() {
  const { user } = useAuth();
  const { active: activeCtx, managed, ready } = useAppContexts();
  const activeCafeId = ready && activeCtx.kind === "cafe" ? activeCtx.id : null;
  const [loading, setLoading] = useState(true);
  const [cafeName, setCafeName] = useState("");
  const [cafeId, setCafeId] = useState<string | null>(null);
  const [cafeSlug, setCafeSlug] = useState<string | null>(null);
  const [profileIncomplete, setProfileIncomplete] = useState(false);
  const [events, setEvents] = useState<EventRow[]>([]);
  const [myCafeEvents, setMyCafeEvents] = useState<MyCafeEvent[]>([]);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [weekEventsCount, setWeekEventsCount] = useState(0);
  const [weekTotal, setWeekTotal] = useState(0);
  const [topClubs, setTopClubs] = useState<TopClub[]>([]);
  const [contactClub, setContactClub] = useState<TopClub | null>(null);
  const [activeTab, setActiveTab] = useState<"resumen" | "cafe" | "menu">("resumen");


  useEffect(() => {
    if (!user) return;
    if (ready && managed.length > 0 && !activeCafeId) return;
    (async () => {
      const base = supabase
        .from("cafes")
        .select("id,name,slug,location,hours,description,opens_at,closes_at");
      const { data: cafe, error: cafeError } = await (activeCafeId
        ? base.eq("id", activeCafeId).maybeSingle()
        : base.eq("owner_id", user.id).maybeSingle());

      if (cafeError) {
        console.error("Error cargando cafetería:", cafeError.message);
        setLoading(false);
        return;
      }

      if (!cafe) {
        setProfileIncomplete(true);
        setLoading(false);
        return;
      }
      setCafeName(cafe.name ?? "");
      setCafeId(cafe.id);
      setCafeSlug(cafe.slug ?? null);
      setProfileIncomplete(
        !cafe.location ||
          !cafe.description ||
          (!cafe.hours && !(cafe.opens_at && cafe.closes_at)),
      );


      const today = new Date().toISOString().split("T")[0];
      const { data: evs } = await supabase
        .from("events")
        .select("id,name,event_date,event_time,meeting_point,estado_confirmacion,created_at,organizador_tipo,clubs(name,type)")
        .eq("cafe_id", cafe.id)
        .eq("organizador_tipo", "club")
        .gte("event_date", today)
        .order("event_date");
      const list = (evs ?? []) as unknown as EventRow[];
      setEvents(list);

      // Eventos creados por la propia cafetería
      const { data: myEvs } = await supabase
        .from("events")
        .select("id,name,event_date,event_time,tipo_actividad,invitaciones_clubs(club_id,estado,clubs(name))")
        .eq("organizer_id", user.id)
        .eq("organizador_tipo", "cafeteria")
        .gte("event_date", today)
        .order("event_date");
      setMyCafeEvents(
        ((myEvs ?? []) as unknown as Array<{
          id: string;
          name: string;
          event_date: string;
          event_time: string;
          tipo_actividad: MyCafeEvent["tipo_actividad"];
          invitaciones_clubs: MyCafeEvent["invitaciones"];
        }>).map((e) => ({
          id: e.id,
          name: e.name,
          event_date: e.event_date,
          event_time: e.event_time,
          tipo_actividad: e.tipo_actividad,
          invitaciones: e.invitaciones_clubs ?? [],
        })),
      );

      const { data: allEvs } = await supabase
        .from("events")
        .select("club_id,clubs(id,name,city,owner_id)")
        .eq("cafe_id", cafe.id);
      const freq: Record<string, { club: Omit<TopClub, "visits" | "organizer_name" | "phone" | "contact_email"> & { phone: string | null; contact_email: string | null }; visits: number }> = {};
      (allEvs ?? []).forEach((e) => {
        const c = (e as unknown as { clubs: { id: string; name: string; city: string | null; owner_id: string } | null }).clubs;
        if (!c) return;
        if (!freq[c.id]) {
          freq[c.id] = { club: { ...c, phone: null, contact_email: null }, visits: 0 };
        }
        freq[c.id].visits += 1;
      });
      const ownerIds = Object.values(freq).map((f) => f.club.owner_id);
      const profMap: Record<string, string | null> = {};
      if (ownerIds.length) {
        const { data: profs } = await supabase
          .from("profiles")
          .select("id,full_name")
          .in("id", ownerIds);
        (profs ?? []).forEach((p: { id: string; full_name: string | null }) => {
          profMap[p.id] = p.full_name;
        });
      }
      setTopClubs(
        Object.values(freq)
          .map((f) => ({
            ...f.club,
            visits: f.visits,
            organizer_name: profMap[f.club.owner_id] ?? null,
          }))
          .sort((a, b) => b.visits - a.visits)
          .slice(0, 6),
      );

      const weekFromNow = new Date();
      weekFromNow.setDate(weekFromNow.getDate() + 7);
      const weekISO = weekFromNow.toISOString().split("T")[0];
      const weekList = list.filter((e) => e.event_date <= weekISO);
      setWeekEventsCount(weekList.length);

      if (list.length) {
        const { data: ins } = await supabase
          .from("inscripciones")
          .select("evento_id")
          .in("evento_id", list.map((e) => e.id));
        const c: Record<string, number> = {};
        (ins ?? []).forEach((i: { evento_id: string }) => {
          c[i.evento_id] = (c[i.evento_id] ?? 0) + 1;
        });
        setCounts(c);
        setWeekTotal(weekList.reduce((acc, e) => acc + (c[e.id] ?? 0), 0));
      }
      setLoading(false);
    })();
  }, [user, ready, activeCafeId, managed.length]);

  // El contexto activo no es un café: manda al panel correspondiente.
  if (ready && managed.length > 0 && !activeCafeId) {
    return (
      <div className="mx-auto max-w-6xl px-4 md:px-6 py-20 text-muted-foreground space-y-4">
        <p>Estás en el contexto “{activeCtx.name}”. Cambia al contexto de tu café para gestionarlo.</p>
        <Link to="/dashboard" className="text-primary hover:underline">Ir a mi panel</Link>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="mx-auto max-w-6xl px-4 md:px-6 py-20 text-muted-foreground">Cargando...</div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl px-4 md:px-6 py-12 space-y-12">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <p className="text-primary text-xs tracking-[0.3em] uppercase mb-2">cafetería brulo</p>
          <h1 className="font-display text-4xl sm:text-5xl break-words">
            {cafeName ? `Bienvenido, ${cafeName}` : "Bienvenido"}
          </h1>
        </div>
        <Link
          to="/cafe/eventos/nuevo"
          className="inline-flex items-center justify-center gap-2 px-5 py-3 min-h-12 rounded-md bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90 whitespace-normal text-center"
        >
          <Sparkles className="w-4 h-4 shrink-0" /> Crear evento especial
        </Link>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-border overflow-x-auto pt-2">
        <TabButton active={activeTab === "resumen"} onClick={() => setActiveTab("resumen")} icon={<CalendarDays className="w-4 h-4" />} label="Resumen" />
        <TabButton active={activeTab === "cafe"} onClick={() => setActiveTab("cafe")} icon={<Palette className="w-4 h-4" />} label="Mi café" />
        <TabButton active={activeTab === "menu"} onClick={() => setActiveTab("menu")} icon={<Utensils className="w-4 h-4" />} label="Mi menú" />
      </div>

      {activeTab === "cafe" && cafeId && (
        <div className="space-y-6">
          <section className="bg-card border border-border rounded-xl p-6">
            <h2 className="font-display text-xl mb-4 flex items-center gap-2">
              <ExternalLink className="w-5 h-5 text-primary" />
              Tu página pública
            </h2>
            <p className="text-sm text-muted-foreground mb-4">
              Comparte este enlace para atraer atletas a tu café.
            </p>
            <div className="flex flex-col gap-3">
              <div className="flex-1 min-w-0 bg-background border border-border rounded-lg px-4 py-2.5 text-sm text-foreground truncate">
                https://brulo.fit/cafe/{cafeSlug ?? cafeId}
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(`https://brulo.fit/cafe/${cafeSlug ?? cafeId}`);
                    toast.success("¡Enlace copiado!");
                  }}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90 transition"
                >
                  <Copy className="w-4 h-4" />
                  Copiar enlace
                </button>
                <a
                  href={`https://brulo.fit/cafe/${cafeSlug ?? cafeId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 border-primary text-primary text-sm font-semibold hover:bg-primary/10 transition"
                >
                  <ExternalLink className="w-4 h-4" />
                  Ver mi página
                </a>
              </div>
            </div>
          </section>

          <CustomizeCafeSection cafeId={cafeId} />
        </div>
      )}

      {activeTab === "menu" && cafeId && (
        <div className="space-y-6">
          <CafeMenuManager cafeId={cafeId} />
        </div>
      )}

      {activeTab === "resumen" && (
      <>
      {profileIncomplete && (

        <Link
          to="/cafe/perfil"
          className="block bg-primary/10 border border-primary/40 rounded-lg p-5 hover:bg-primary/15 transition"
        >
          <p className="text-primary font-medium">
            Completa tu perfil para aparecer en el mapa de Brulo →
          </p>
        </Link>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 w-full">
        <StatCard icon={<CalendarDays className="w-7 h-7" />} value={weekEventsCount} label="eventos esta semana" />
        <StatCard icon={<Footprints className="w-7 h-7" />} value={weekTotal} label="deportistas esperados" highlight />
        <StatCard icon={<Users className="w-7 h-7" />} value={topClubs.length} label="clubs que te visitan" />
      </div>

      <div className="flex items-center gap-3 px-5 py-4 rounded-lg bg-primary/5 border border-primary/20">
        <Sparkles className="w-5 h-5 text-primary shrink-0" />
        <p className="text-sm text-muted-foreground">
          {weekEventsCount === 0
            ? <>Tu comunidad <span className="text-foreground font-semibold">está por llegar</span>.</>
            : weekEventsCount <= 5
              ? <>Tu café <span className="text-foreground font-semibold">está en el mapa</span>.</>
              : <>Eres un <span className="text-primary font-semibold">punto Brulo de referencia</span>.</>}
        </p>
      </div>

      <PendingRequestsSection
        events={events.filter((e) => e.estado_confirmacion === "pendiente")}
        counts={counts}
        onRespond={async (id, estado) => {
          const { error } = await supabase
            .from("events")
            .update({ estado_confirmacion: estado, confirmacion_respondida_at: new Date().toISOString() })
            .eq("id", id);
          if (error) {
            toast.error(error.message);
            return;
          }
          toast.success(estado === "confirmado" ? "Solicitud confirmada" : "Solicitud declinada");
          setEvents((evs) => evs.map((e) => (e.id === id ? { ...e, estado_confirmacion: estado } : e)));
        }}
      />

      {myCafeEvents.length > 0 && (
        <Section title="Mis eventos creados" icon={<Sparkles className="w-6 h-6 text-primary" />}>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 w-full">
            {myCafeEvents.map((ev) => {
              const aceptados = ev.invitaciones.filter((i) => i.estado === "aceptado").length;
              const pendientes = ev.invitaciones.filter((i) => i.estado === "pendiente").length;
              const declinados = ev.invitaciones.filter((i) => i.estado === "declinado").length;
              return (
                <Link
                  key={ev.id}
                  to="/cafe/eventos/$eventId"
                  params={{ eventId: ev.id }}
                  className="bg-card border border-primary/30 rounded-lg p-6 hover:border-primary transition block min-w-0 overflow-hidden"
                >
                  <span className="inline-block text-[10px] uppercase tracking-[0.2em] font-bold px-2 py-1 rounded bg-primary/20 text-primary mb-3">
                    Evento del local
                  </span>
                  <h3 className="font-display text-xl break-words">{ev.name}</h3>
                  <p className="text-sm text-muted-foreground mt-2">
                    {new Date(`${ev.event_date}T00:00:00`).toLocaleDateString("es-MX", {
                      weekday: "long",
                      day: "numeric",
                      month: "long",
                    })}{" "}
                    · {ev.event_time?.slice(0, 5)}
                  </p>
                  {ev.invitaciones.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-border text-xs text-muted-foreground space-y-1">
                      <p>
                        <span className="text-primary font-semibold">{aceptados}</span> aceptados ·{" "}
                        <span className="text-amber-400 font-semibold">{pendientes}</span> pendientes
                        {declinados > 0 && (
                          <>
                            {" "}· <span className="text-red-400 font-semibold">{declinados}</span> declinados
                          </>
                        )}
                      </p>
                    </div>
                  )}
                </Link>
              );
            })}
          </div>
        </Section>
      )}

      <Section title="Próximas visitas" icon={<Calendar className="w-6 h-6 text-primary" />}>
        {events.filter((e) => e.estado_confirmacion === "confirmado").length === 0 ? (
          <Empty text="Aún no hay eventos confirmados con tu cafetería como punto de reunión." />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 w-full">
            {events.filter((e) => e.estado_confirmacion === "confirmado").map((ev) => (
              <Link
                key={ev.id}
                to="/cafe/eventos/$eventId"
                params={{ eventId: ev.id }}
                className="bg-card border border-border rounded-lg p-6 hover:border-primary/50 transition block"
              >
                <div className="mb-2"><EventStatusBadge estado={ev.estado_confirmacion} createdAt={ev.created_at} /></div>
                <h3 className="font-display text-xl">{ev.name}</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  {ev.clubs?.name ?? "Club"}
                </p>
                <p className="text-sm text-muted-foreground mt-2">
                  {new Date(ev.event_date).toLocaleDateString("es-MX", {
                    weekday: "long",
                    day: "numeric",
                    month: "long",
                  })}{" "}
                  · {ev.event_time?.slice(0, 5)}
                </p>
                <div className="mt-4 pt-4 border-t border-border text-sm">
                  <strong className="text-primary">{counts[ev.id] ?? 0}</strong>{" "}
                  <span className="text-muted-foreground">inscritos</span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </Section>

      <Section title="Clubs frecuentes" icon={<Users className="w-6 h-6 text-primary" />}>
        {topClubs.length === 0 ? (
          <Empty text="Aún no hay clubs frecuentes." />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 w-full">
            {topClubs.map((c) => (
              <div key={c.id} className="bg-card border border-border rounded-lg p-5 flex flex-col">
                <h3 className="font-display text-xl">{c.name}</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  {c.visits} {c.visits === 1 ? "visita" : "visitas"}
                </p>
                <button
                  onClick={async () => {
                    const { data } = await supabase.rpc("get_club_contact", { _club_id: c.id });
                    const contact = (data as { phone: string | null; contact_email: string | null }[] | null)?.[0];
                    setContactClub({ ...c, phone: contact?.phone ?? null, contact_email: contact?.contact_email ?? null });
                  }}
                  className="mt-4 text-sm text-primary hover:underline self-start"
                >
                  Contactar organizador →
                </button>
              </div>
            ))}
          </div>
        )}
      </Section>
      </>
      )}

      {contactClub && (
        <ContactClubModal
          club={contactClub}
          cafeName={cafeName}
          onClose={() => setContactClub(null)}
        />
      )}
    </div>
  );
}

function TabButton({ active, onClick, icon, label }: { active: boolean; onClick: () => void; icon: React.ReactNode; label: string }) {
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 transition whitespace-nowrap ${active ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}
    >
      {icon} {label}
    </button>
  );
}


function ContactClubModal({
  club,
  cafeName,
  onClose,
}: {
  club: TopClub;
  cafeName: string;
  onClose: () => void;
}) {
  const initial = (club.name || "?").charAt(0).toUpperCase();
  const hasContact = !!(club.phone || club.contact_email);
  const message = `Hola, soy ${cafeName} en Brulo. Me gustaría proponer una colaboración con ${club.name} 🏃☕`;
  const waNumber = club.phone?.replace(/[^\d]/g, "") ?? "";
  const waUrl = `https://wa.me/${waNumber}?text=${encodeURIComponent(message)}`;

  const copyEmail = async () => {
    if (!club.contact_email) return;
    try {
      await navigator.clipboard.writeText(club.contact_email);
      toast.success("Email copiado");
    } catch {
      toast.error("No pudimos copiar el email");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-card border border-border rounded-xl max-w-md w-full p-6 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-muted-foreground hover:text-foreground"
          aria-label="Cerrar"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="flex items-center gap-4 mb-5">
          <div className="w-14 h-14 rounded-full bg-primary/15 border border-primary/30 flex items-center justify-center font-display text-2xl text-primary">
            {initial}
          </div>
          <div>
            <h3 className="font-display text-2xl">{club.name}</h3>
            {club.city && (
              <p className="text-xs text-muted-foreground">{club.city}</p>
            )}
          </div>
        </div>

        <div className="space-y-1 mb-6 text-sm">
          <p>
            <span className="text-muted-foreground">Organizador: </span>
            <span>{club.organizer_name || "Sin nombre"}</span>
          </p>
          <p className="text-muted-foreground">
            {club.visits} {club.visits === 1 ? "evento realizado" : "eventos realizados"} en tu cafetería
          </p>
        </div>

        {hasContact ? (
          <div className="space-y-2">
            {club.phone && (
              <a
                href={waUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full bg-primary text-primary-foreground hover:opacity-90 transition rounded-md px-4 py-3 flex items-center justify-center gap-2 font-medium"
              >
                <MessageCircle className="w-4 h-4" />
                Enviar mensaje por WhatsApp
              </a>
            )}
            {club.contact_email && (
              <button
                onClick={copyEmail}
                className="w-full bg-secondary text-secondary-foreground hover:bg-secondary/80 transition rounded-md px-4 py-3 flex items-center justify-center gap-2 font-medium"
              >
                <Copy className="w-4 h-4" />
                Copiar email del organizador
              </button>
            )}
            {club.contact_email && (
              <p className="text-xs text-muted-foreground text-center pt-1 flex items-center justify-center gap-1.5">
                <Mail className="w-3 h-3" />
                {club.contact_email}
              </p>
            )}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground text-center bg-muted/40 rounded-md p-4">
            Este organizador aún no ha compartido sus datos de contacto.
          </p>
        )}
      </div>
    </div>
  );
}

function StatCard({
  value,
  label,
  highlight,
  icon,
}: {
  value: number;
  label: string;
  highlight?: boolean;
  icon?: React.ReactNode;
}) {
  return (
    <div className="bg-card border border-border rounded-xl p-6 transition-colors hover:border-primary/40">
      {icon && (
        <div className={`mb-3 inline-flex p-2.5 rounded-lg ${highlight ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"}`}>
          {icon}
        </div>
      )}
      <p className={`font-display text-5xl ${highlight ? "text-primary" : "text-foreground"}`}>
        {value}
      </p>
      <p className="text-xs text-muted-foreground mt-2 uppercase tracking-widest">{label}</p>
    </div>
  );
}

function Section({
  title,
  icon,
  children,
}: {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section>
      <div className="flex items-center gap-3 mb-5">
        {icon}
        <h2 className="font-display text-3xl">{title}</h2>
      </div>
      {children}
    </section>
  );
}

function Empty({ text }: { text: string }) {
  return (
    <div className="bg-card border border-border rounded-lg p-8 text-center text-muted-foreground">
      <Coffee className="w-8 h-8 mx-auto mb-3 opacity-40" />
      {text}
    </div>
  );
}

function PendingRequestsSection({
  events,
  counts,
  onRespond,
}: {
  events: EventRow[];
  counts: Record<string, number>;
  onRespond: (id: string, estado: "confirmado" | "declinado") => void | Promise<void>;
}) {
  if (events.length === 0) return null;
  return (
    <Section title="Solicitudes pendientes" icon={<Bell className="w-6 h-6 text-primary" />}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full">
        {events.map((ev) => (
          <div key={ev.id} className="bg-amber-500/5 border border-amber-500/30 rounded-lg p-6">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-amber-400 mb-1">
                  {ev.clubs?.name ?? "Club"}
                </p>
                <h3 className="font-display text-xl">{ev.name}</h3>
              </div>
              <EventStatusBadge estado={ev.estado_confirmacion} createdAt={ev.created_at} />
            </div>
            <p className="text-sm text-muted-foreground">
              {new Date(ev.event_date).toLocaleDateString("es-MX", {
                weekday: "long",
                day: "numeric",
                month: "long",
              })}{" "}
              · {ev.event_time?.slice(0, 5)}
            </p>
            <p className="text-sm text-muted-foreground mt-1">
              <strong className="text-foreground">{counts[ev.id] ?? 0}</strong>{" "}
              {(counts[ev.id] ?? 0) === 1 ? "asistente esperado" : "asistentes esperados"}
            </p>
            <div className="mt-5 flex gap-2">
              <button
                onClick={() => onRespond(ev.id, "confirmado")}
                className="flex-1 px-4 py-2 rounded-md bg-primary text-primary-foreground font-semibold hover:opacity-90 inline-flex items-center justify-center gap-1.5"
              >
                <Check className="w-4 h-4" /> Confirmar
              </button>
              <button
                onClick={() => onRespond(ev.id, "declinado")}
                className="flex-1 px-4 py-2 rounded-md bg-muted text-muted-foreground font-semibold hover:bg-muted/80 inline-flex items-center justify-center gap-1.5"
              >
                <X className="w-4 h-4" /> Declinar
              </button>
            </div>
          </div>
        ))}
      </div>
    </Section>
  );
}

